import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button, Skeleton } from '@aimsos/ui';

import { AccessDeniedState, EmptyState } from '../states/index.js';
import { INSIGHTS_LIST_STATE } from '../../utils/insightsListState.js';
import InsightEmergingToggle from './InsightEmergingToggle.jsx';
import InsightGrid, { INSIGHT_GRID_CLASS } from './InsightGrid.jsx';

import { SearchX } from 'lucide-react';

const SKELETON_CARDS = [0, 1, 2, 3];

/**
 * Whatever the Insights subtab is showing (CT-5716): the grid, the skeleton, or
 * one of the states that are not cards.
 *
 * Which of them is on screen is decided by `insightsListState` and arrives as
 * one value, so this component only paints — it re-derives nothing. The page
 * keeps the toolbar, the notices, the "Couldn't load insights" row and the
 * chips; with nothing to show under that row, this paints nothing.
 *
 * Whether the emerging insights are open is the page's (`showEmerging`): closed
 * by default, open by default while no pattern holds yet — then they ARE the
 * content — and the user can toggle it either way.
 *
 * @param {object} props
 * @param {string} props.state — one of `INSIGHTS_LIST_STATE`.
 * @param {Array<object>} props.items
 * @param {{ count: number, items: Array<object> }} props.emerging
 * @param {boolean} props.showEmerging
 * @param {() => void} props.onToggleEmerging
 * @param {string|null} [props.openInsightId] — the insight open in the slide-out.
 * @param {(insight: object) => void} props.onOpenInsight
 * @param {() => void} props.onClearFilters
 */
const InsightsResults = memo(function InsightsResults({
  state,
  items,
  emerging,
  showEmerging,
  onToggleEmerging,
  openInsightId = null,
  onOpenInsight,
  onClearFilters,
}) {
  const { t } = useTranslation('unified-profiles');

  if (state === INSIGHTS_LIST_STATE.LOADING) {
    return (
      <div data-testid="insights-loading" className={INSIGHT_GRID_CLASS} aria-busy>
        {SKELETON_CARDS.map((key) => (
          <Skeleton key={key} width="100%" height="180px" borderRadius="12px" />
        ))}
      </div>
    );
  }
  if (state === INSIGHTS_LIST_STATE.ACCESS_DENIED) return <AccessDeniedState />;
  if (state === INSIGHTS_LIST_STATE.ERROR) return null;

  if (state === INSIGHTS_LIST_STATE.NO_RESULTS || state === INSIGHTS_LIST_STATE.EMPTY) {
    return (
      <EmptyState
        testId="no-results-state"
        icon={SearchX}
        title={t('intelligence.insights.noResults.title')}
        subtitle={t('intelligence.insights.noResults.subtitle')}
        action={
          state === INSIGHTS_LIST_STATE.NO_RESULTS ? (
            <Button variant="secondary" size="sm" onClick={onClearFilters}>
              {t('intelligence.insights.noResults.clear')}
            </Button>
          ) : null
        }
      />
    );
  }

  // Rows, or no pattern yet: then only the emerging insights, behind their toggle.
  return (
    <div className="flex flex-col gap-4">
      {items.length ? <InsightGrid cards={items} openInsightId={openInsightId} onOpen={onOpenInsight} /> : null}
      {emerging.count ? (
        <>
          <InsightEmergingToggle count={emerging.count} expanded={showEmerging} onToggle={onToggleEmerging} />
          {showEmerging ? <InsightGrid cards={emerging.items} openInsightId={openInsightId} onOpen={onOpenInsight} /> : null}
        </>
      ) : null}
    </div>
  );
});

export default InsightsResults;
